<template>
  <div ref="scrollerRef" class="grow overflow-y-auto" @scroll="updateScrollTop">
    <div :style="{ height: `${totalHeight}px` }">
      <div :style="{ translate: `0 ${scrollOffset}px` }">
        <template v-for="{ item, index } in visibleItems" :key="item.id">
          <div
            :ref="measureElement"
            class="contain-content"
            :data-index="index"
          >
            <slot name="row" :item="item" :index="index"></slot>
          </div>
        </template>
      </div>
    </div>

    <slot name="footer"></slot>
  </div>
</template>

<script setup lang="ts" generic="Model extends GenericModel">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRef,
  watch,
} from "vue";

import type { NormalizedDataSource } from "@/types/data-source";
import type { GenericModel } from "@/types/model";

import type { RowProps } from "./list-types";

const BUFFER = 5;

const props = withDefaults(
  defineProps<{
    source: NormalizedDataSource<Model>;
    estimateHeight?: number;
  }>(),
  {
    estimateHeight: 26,
  }
);

defineSlots<{
  row(props: RowProps<Model>): unknown;
  footer(props: {}): unknown;
}>();

const items = toRef(() => props.source.data);
const estimateHeight = toRef(props, "estimateHeight");

const scrollerRef = shallowRef<HTMLDivElement>();

const scrollTop = shallowRef(0);
const scrollWindowHeight = shallowRef<number>();

const elemCache = new Map<number, HTMLElement>();
const heightCache = ref<number[]>([]);
watch(
  () => items.value.length,
  (length) => {
    const curr = heightCache.value;
    const currLength = curr.length;

    if (length > currLength) {
      heightCache.value = curr.concat(
        new Array(length - currLength).fill(estimateHeight.value)
      );
    } else if (length < currLength) {
      heightCache.value = curr.slice(0, length);
    }
  },
  { immediate: true }
);

const cumulativeHeights = computed(() => {
  const $heightCache = heightCache.value;

  const cumulative: number[] = [];
  let total = 0;

  for (let idx = 0, len = $heightCache.length; idx < len; idx++) {
    const height = $heightCache[idx];
    cumulative.push((total += height));
  }

  return cumulative;
});

interface VisibleRange {
  start: number;
  end: number;
}

const visibleRange = computed((prev?: VisibleRange): VisibleRange => {
  const $scrollWindowHeight = scrollWindowHeight.value;
  if ($scrollWindowHeight === undefined) {
    return { start: 0, end: BUFFER * 2 };
  }

  const $scrollTop = scrollTop.value;
  const $heights = cumulativeHeights.value;

  const start = binarySearch($heights, $scrollTop, 0);
  const end = binarySearch($heights, $scrollTop + $scrollWindowHeight, start);

  const range: VisibleRange = {
    start: Math.max(0, start - BUFFER),
    end: Math.min($heights.length, end + 1 + BUFFER),
  };

  // Reuse the previous range object if it's the same, don't have
  // to recompute downstream.
  if (prev && prev.start === range.start && prev.end === range.end) {
    return prev;
  }

  return range;
});

const visibleItems = computed(() => {
  const { start, end } = visibleRange.value;

  return items.value.slice(start, end).map((item, idx) => {
    return {
      item: item,
      index: start + idx,
    };
  });
});

const totalHeight = computed(() => {
  const $heights = cumulativeHeights.value;
  return $heights.length > 0 ? $heights[$heights.length - 1] : 0;
});

const scrollOffset = computed(() => {
  const $start = visibleRange.value.start;
  return $start === 0 ? 0 : cumulativeHeights.value[$start - 1];
});

const rowRO = new ResizeObserver((entries) => {
  const $heightCache = heightCache.value;

  for (let idx = 0, len = entries.length; idx < len; idx++) {
    const entry = entries[idx];

    const target = entry.target as HTMLElement;
    if (!target.isConnected) {
      // Stale observer entries, these will report as 0px rect height.
      rowRO.unobserve(target);
      continue;
    }

    const index = indexFromElement(target);
    if (index === null) {
      continue;
    }

    const height = entry.contentRect.height;
    $heightCache[index] = height;
  }
});

const updateScrollTop = () => {
  scrollTop.value = scrollerRef.value!.scrollTop;
};

const updateContainerHeight = () => {
  scrollWindowHeight.value = scrollerRef.value!.clientHeight;
};

const measureElement = (rawNode: any) => {
  const node = rawNode as HTMLElement | null;

  if (node) {
    const index = indexFromElement(node);
    if (index === null) {
      return;
    }

    const prevNode = elemCache.get(index);
    if (node !== prevNode) {
      if (prevNode) {
        rowRO.unobserve(prevNode);
      }

      rowRO.observe(node);
      elemCache.set(index, node);
    }

    return;
  }

  elemCache.forEach((cachedElem, key) => {
    if (!cachedElem.isConnected) {
      rowRO.unobserve(cachedElem);
      elemCache.delete(key);
    }
  });
};

onMounted(() => {
  const scrollerRO = new ResizeObserver(updateContainerHeight);
  scrollerRO.observe(scrollerRef.value!);

  onUnmounted(() => {
    scrollerRO.disconnect();
    rowRO.disconnect();
  });
});

const indexFromElement = (node: HTMLElement) => {
  const rawIndex = node.dataset.index as string | undefined;
  if (rawIndex === undefined) {
    return null;
  }

  return parseInt(rawIndex, 10);
};

const binarySearch = (arr: number[], target: number, low: number): number => {
  let high = arr.length - 1;
  let ans = arr.length;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (arr[mid] >= target) {
      ans = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return ans;
};
</script>
